import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowDownToLine } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function TopupDialog({ open, onOpenChange, card, onDone }) {
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const handleTopup = async () => {
    const num = parseFloat(amount);
    if (!num || num <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setLoading(true);
    try {
      await api.post(`/cards/${card.id}/topup-request`, { amount: num, merchant: "Wallet Top-up", password: "" });
      toast.success(`Top-up request for $${num.toFixed(2)} sent for approval`, { id: "topup-request" });
      setAmount("");
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Top-up request failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setAmount(""); onOpenChange(o); }}>
      <DialogContent className="bg-[#121216] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight flex items-center gap-2">
            <ArrowDownToLine className="w-5 h-5 text-[#00FF9D]" /> Request Top-up
          </DialogTitle>
          <DialogDescription className="text-[#A0A0AB]">
            Request funds for {card?.cardholder_name}'s card. An admin must approve before the balance is credited.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Amount (USD)</Label>
            <Input
              data-testid="topup-amount-input"
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleTopup()}
              className="bg-[#1A1A20] border-white/10 text-white h-12 font-mono-sig"
            />
          </div>
          <div className="flex gap-2">
            {[50, 100, 250, 500].map((v) => (
              <button
                key={v}
                data-testid={`topup-quick-${v}`}
                onClick={() => setAmount(String(v))}
                className="flex-1 h-10 rounded-xl border border-white/10 bg-white/5 text-sm font-mono-sig hover:bg-white/10 transition-colors"
              >
                ${v}
              </button>
            ))}
          </div>
          <Button
            data-testid="topup-submit-button"
            onClick={handleTopup}
            disabled={loading}
            className="w-full h-12 bg-[#00FF9D] text-black hover:bg-[#00FF9D]/90 font-semibold rounded-xl"
          >
            <ArrowDownToLine className="w-4 h-4 mr-2" /> {loading ? "Sending…" : "Request Top-up"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
